import {
  AppBar,
  AppBarActions,
} from '@twistopayments/design-system/components/atoms/app-bar'
import { IconButton } from '@twistopayments/design-system/components/atoms/button'
import { Logo } from '@twistopayments/design-system/components/atoms/logo'
import { CloseIcon } from '@twistopayments/design-system/icons/inline'
import { useAtom } from 'jotai'
import { useRouter } from 'next/router'
import { isNil } from 'ramda'

import { useSubmitCancelPsd2MandatoryMutation } from '@twisto/api-graphql'
import { useGetFlowData } from '@twisto/flow-views'
import {
  submittingFlowFormAtom,
  useSubmittingWrapper,
} from '@twisto/flow-views/components'
import {
  rlcRequestOptions,
  useRlcSessionAction,
} from '@twisto/flow-views/rlc/rlc-action.hook'
import { RlcError } from '@twisto/flow-views/rlc/rlc-error'
import { useIsMobile, useOpen } from '@twisto/hooks'

import { FlowLeaveConfirmModal } from '../flow-leave-confirm-modal'

import { logCloseButtonClick } from './close-button-analytics'
import * as styles from './flow-app-bar.styles'
import { FlowGoBackButton } from './flow-go-back-button'

type Props = {
  isPayInNLayout?: boolean
}

export const FlowAppBar = ({ isPayInNLayout = false }: Props) => {
  const [submitting] = useAtom(submittingFlowFormAtom)
  const isMobile = useIsMobile()
  const { isOpen, handleClose, handleOpen } = useOpen()
  const router = useRouter()
  const sessionId = router.query.sessionId as string
  const { flow } = useGetFlowData(sessionId)
  const [cancelPsd2Mandatory] =
    useSubmitCancelPsd2MandatoryMutation(rlcRequestOptions)
  const cancellation = useRlcSessionAction(sessionId)
  const submittingWrapper = useSubmittingWrapper()
  const closeAction =
    flow?.currentStep.__typename === 'Psd2MandatoryPromptStep'
      ? {
          handleCancel: () => {
            logCloseButtonClick(flow.currentStep)

            return submittingWrapper(
              cancellation.run(() =>
                cancelPsd2Mandatory({
                  variables: {
                    auth: { sessionId },
                    input: { confirmed: true },
                  },
                })
              )
            )
          },
          disabled:
            submitting || cancellation.pending || cancellation.requiresRefresh,
          error: <RlcError {...cancellation} />,
        }
      : {
          handleCancel: handleOpen,
          disabled: submitting,
          error: null,
        }

  const hasPurchaseContext = !isNil(flow?.context)

  return (
    <>
      {/* Confirmation modal for leave/cancel action */}
      <FlowLeaveConfirmModal
        currentStep={flow?.currentStep.__typename ?? ''}
        flowType={flow?.flowType}
        hasPurchaseContext={hasPurchaseContext}
        open={isOpen}
        onClose={(options) => {
          if (!options?.skipAnalytics) {
            logCloseButtonClick(flow?.currentStep)
          }

          handleClose()
        }}
      />
      <AppBar
        css={styles.appBar}
        variant={isPayInNLayout ? 'light' : 'contained'}
      >
        <AppBarActions position="left">
          <FlowGoBackButton />
        </AppBarActions>

        <Logo variant="full" />

        <AppBarActions position="right">
          {flow?.canCancel === true && (
            <IconButton
              color="neutral"
              data-testid="session-close"
              disabled={closeAction.disabled}
              icon={CloseIcon}
              size={isMobile ? 'small' : 'medium'}
              onClick={closeAction.handleCancel}
            />
          )}
        </AppBarActions>
      </AppBar>
      {closeAction.error}
    </>
  )
}
